import { BlurText } from "@/components/common/BlurText";
import { ConnectionLost } from "@/components/common/ConnectionLost";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { useEnrollments } from "@/hooks/useEnrollments";
import { createCheckout, getCourse } from "@/lib/api";
import { NetworkError } from "@/lib/api-types";
import { queryKeys } from "@/lib/query-keys";
import { cn, formatDuration, formatPrice } from "@/lib/utils";
import { useAuth } from "@clerk/clerk-react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, ArrowRight, BookOpen, ChevronDown, Clock, Loader2 } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";

export function CourseDetail() {
	const { t } = useTranslation();
	const { courseId: courseIdParam } = useParams<{ courseId: string }>();
	const courseId = courseIdParam ?? "";
	const { userId } = useAuth();
	const [discountCode, setDiscountCode] = useState("");
	const [isCheckingOut, setIsCheckingOut] = useState(false);

	const {
		data: course,
		isLoading,
		isError,
		error,
	} = useQuery({
		queryKey: queryKeys.courses.detail(courseId),
		queryFn: () => getCourse(courseId),
		enabled: !!courseId,
	});

	const { data: enrollments } = useEnrollments();
	const enrollment = enrollments?.find((e) => e.course_id === courseId);
	const isEnrolled = enrollment?.status === "active";
	const isDisabled = enrollment?.status === "disabled";

	// Collapsible section state: for <= 3 sections all expanded, otherwise only first
	const sectionCount = course?.sections?.length ?? 0;
	const defaultExpanded = useMemo(() => {
		const sections = course?.sections;
		if (!sections) return new Set<string>();
		if (sectionCount <= 3) {
			return new Set(sections.map((s) => s.id));
		}
		const first = sections[0];
		return new Set(first ? [first.id] : []);
	}, [course?.sections, sectionCount]);

	const [expandedSections, setExpandedSections] = useState<Set<string> | null>(null);
	const expanded = expandedSections ?? defaultExpanded;

	function toggleSection(sectionId: string) {
		const current = expandedSections ?? new Set(defaultExpanded);
		const next = new Set(current);
		if (next.has(sectionId)) {
			next.delete(sectionId);
		} else {
			next.add(sectionId);
		}
		setExpandedSections(next);
	}

	const handleEnroll = async () => {
		if (!courseId || !userId) return;
		setIsCheckingOut(true);

		try {
			const { checkout_url } = await createCheckout({
				course_id: courseId,
				user_id: userId,
				discount_code: discountCode || undefined,
			});
			window.location.href = checkout_url;
		} catch {
			setIsCheckingOut(false);
			toast.error(t("error.somethingWrong"));
		}
	};

	if (isLoading) {
		return (
			<div className="mx-auto max-w-4xl px-4 py-8 lg:px-8" aria-busy="true">
				<Skeleton className="mb-4 h-10 w-2/3 rounded-lg" />
				<Skeleton className="mb-8 h-6 w-1/3 rounded-md" />
				<Skeleton className="mb-8 h-32 rounded-xl" />
				<div className="space-y-3">
					{Array.from({ length: 5 }).map((_, i) => (
						<Skeleton key={`mod-${i}`} className="h-14 rounded-lg" />
					))}
				</div>
			</div>
		);
	}

	if (isError && error instanceof NetworkError) {
		return (
			<div className="mx-auto max-w-4xl px-4 py-8 lg:px-8">
				<ConnectionLost />
			</div>
		);
	}

	if (isError || !course) {
		return (
			<div className="mx-auto max-w-4xl px-4 py-8 lg:px-8">
				<div
					className="flex flex-col items-center gap-4 rounded-xl border border-white/10 bg-bg-elevated p-12 text-center"
					role="alert"
				>
					<AlertTriangle className="h-10 w-10 text-error" aria-hidden="true" />
					<h2 className="text-lg font-semibold text-primary">{t("error.loadFailed")}</h2>
					<p className="text-sm text-secondary">{t("error.loadFailedDesc")}</p>
				</div>
			</div>
		);
	}

	return (
		<div className="mx-auto max-w-4xl px-4 py-8 lg:px-8">
			{/* Header */}
			<div className="mb-8">
				<div className="mb-4 flex flex-wrap items-center gap-3">
					<Badge variant="outline" className="text-xs uppercase">
						{course.language}
					</Badge>
					<span className="flex items-center gap-1 text-sm text-muted">
						<BookOpen className="h-4 w-4" aria-hidden="true" />
						{t("course.modules", {
							count: course.sections?.reduce((sum, s) => sum + s.modules.length, 0) ?? 0,
						})}
					</span>
					<span className="flex items-center gap-1 text-sm text-muted">
						<Clock className="h-4 w-4" aria-hidden="true" />
						{formatDuration(
							course.sections?.reduce(
								(sum, s) => sum + s.modules.reduce((ms, m) => ms + m.duration_sec, 0),
								0,
							) ?? 0,
						)}
					</span>
				</div>

				<BlurText
					text={course.title}
					className="mb-4 text-3xl font-bold text-primary sm:text-4xl"
				/>

				<p className="text-base text-secondary whitespace-pre-wrap sm:text-lg">
					{course.description}
				</p>
			</div>

			{/* Course content — collapsible sections */}
			<div className="mb-8 space-y-4">
				{course.sections?.map((section) => {
					const isExpanded = expanded.has(section.id);
					return (
						<div
							key={section.id}
							className="rounded-xl border border-white/10 bg-bg-elevated overflow-hidden"
						>
							<button
								type="button"
								onClick={() => toggleSection(section.id)}
								className="flex w-full items-center justify-between px-4 py-3 text-left transition-colors hover:bg-bg-overlay"
							>
								<div className="min-w-0">
									<div className="flex items-center gap-2">
										<h2 className="text-lg font-semibold text-primary">{section.title}</h2>
										<span className="text-xs text-muted">
											{t("course.sectionModules", { count: section.modules.length })}
										</span>
									</div>
									{section.description && (
										<p className="mt-0.5 text-sm text-secondary">{section.description}</p>
									)}
								</div>
								<ChevronDown
									className={cn(
										"ml-2 h-5 w-5 flex-shrink-0 text-secondary transition-transform duration-300 ease-out motion-reduce:transition-none",
										isExpanded && "rotate-180",
									)}
									aria-hidden="true"
								/>
							</button>
							<div
								className={cn(
									"grid transition-[grid-template-rows] duration-300 ease-out motion-reduce:transition-none",
									isExpanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
								)}
							>
								<div className="overflow-hidden">
									<div className="stagger-list space-y-2 px-4 pb-4">
										{section.modules.map((mod) => (
											<div
												key={mod.id}
												className="flex items-center justify-between rounded-lg border border-white/5 bg-bg-surface px-4 py-3"
											>
												<div className="flex items-center gap-3">
													<span className="flex h-7 w-7 items-center justify-center rounded-full bg-bg-overlay text-xs font-medium text-secondary">
														{mod.position}
													</span>
													<span className="text-sm text-primary">{mod.title}</span>
												</div>
												<span className="text-xs text-muted">
													{formatDuration(mod.duration_sec)}
												</span>
											</div>
										))}
									</div>
								</div>
							</div>
						</div>
					);
				})}
			</div>

			{/* CTA section */}
			<div className="rounded-xl border border-white/10 bg-bg-elevated p-4 sm:p-6">
				{isDisabled ? (
					<p className="text-secondary">{t("course.accessDisabled")}</p>
				) : isEnrolled ? (
					<div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
						<div>
							<p className="text-lg font-semibold text-primary">{t("course.enrolled")}</p>
							<p className="text-sm text-secondary">
								{t("course.modulesCompleted", {
									completed: enrollment.modules_completed,
									total: enrollment.modules_total,
								})}
							</p>
						</div>
						<Button asChild>
							<Link to={`/learn/${courseId}`}>
								{t("course.continue")}
								<ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
							</Link>
						</Button>
					</div>
				) : (
					<div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
						<div>
							<p className="text-3xl font-bold text-primary">
								{formatPrice(course.price_cents, course.currency, t("course.free"))}
							</p>
							<div className="mt-3 flex items-center gap-2">
								<label htmlFor="discount-code" className="sr-only">
									{t("course.discountPlaceholder")}
								</label>
								<Input
									id="discount-code"
									placeholder={t("course.discountPlaceholder")}
									value={discountCode}
									onChange={(e) => setDiscountCode(e.target.value)}
									className="w-48"
								/>
							</div>
						</div>
						<Button
							size="lg"
							onClick={handleEnroll}
							disabled={isCheckingOut}
							className="min-h-[48px]"
						>
							{isCheckingOut && (
								<Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
							)}
							{t("course.enroll")}
						</Button>
					</div>
				)}
			</div>
		</div>
	);
}
